<template>
    <page-controls>
        <template v-slot:left>
            <div class="grow @3xl/controls:max-w-md">
                <vx-filterable :active="active"
                    @toggle="toggleFilterable"
                    data-test="filters">
                    <template v-slot:placeholder>
                        <span class="mr-2">Find</span>
                        <vx-badge v-if="filtersCount > 0"
                            data-test="reset"
                            :borders="false"
                            size="xs"
                            @click.stop="reset">
                            {{ pluralized('filter', filtersCount, true )}}
                            <font-awesome-icon widthAuto :icon="faCircleXmark" />
                        </vx-badge>
                    </template>
                    <template v-slot:form>
                        <form @submit.prevent="apply" class="flex flex-col gap-4">
                            <div>
                                <filter-label :canReset="!!filters.query" @reset="filters.query = undefined">
                                    Keyword Search
                                </filter-label>
                                <vx-input type="text"
                                    data-test="query"
                                    placeholder="Search"
                                    maxlength="80"
                                    size="lg"
                                    v-model="filters.query">
                                    <template v-slot:right>
                                        <font-awesome-icon widthAuto class="text-xs" :icon="faMagnifyingGlass" />
                                    </template>
                                </vx-input>
                            </div>

                            <group-status-filter-item v-model="filters.status" />

                            <div>
                                <filter-label :canReset="filters.setup_required !== undefined" @reset="() => {
                                    filters.setup_required = undefined;
                                }">
                                    <span :id="setupLabelId">Setup</span>
                                </filter-label>

                                <div
                                    role="group"
                                    :aria-labelledby="setupLabelId"
                                    class="w-full flex items-center justify-between gap-1 mb-1"
                                >
                                    <vx-checkbox-selection-item :active="setupFilter.required" @selected="setupFilter = { ...setupFilter, required: !setupFilter.required }" class="grow" >
                                        Setup Required
                                    </vx-checkbox-selection-item>
                                    <vx-checkbox-selection-item :active="setupFilter.notRequired" @selected="setupFilter = { ...setupFilter, notRequired: !setupFilter.notRequired }" class="grow" >
                                        Setup Complete
                                    </vx-checkbox-selection-item>
                                </div>
                            </div>

                            <div>
                                <filter-label :canReset="filters.in_org_unit !== undefined" @reset="() => {
                                    filters.in_org_unit = undefined;
                                }">
                                    <span :id="orgUnitLabelId">Organizational Unit</span>
                                </filter-label>

                                <div
                                    role="group"
                                    :aria-labelledby="orgUnitLabelId"
                                    class="w-full flex items-center justify-between gap-1 mb-1"
                                >
                                    <vx-checkbox-selection-item :active="orgUnitFilter.notInOrgUnit" @selected="orgUnitFilter = { ...orgUnitFilter, notInOrgUnit: !orgUnitFilter.notInOrgUnit }" class="grow" >
                                        Not in an OU
                                    </vx-checkbox-selection-item>
                                    <vx-checkbox-selection-item :active="orgUnitFilter.inOrgUnit" @selected="orgUnitFilter = { ...orgUnitFilter, inOrgUnit: !orgUnitFilter.inOrgUnit }" class="grow" >
                                        In an OU
                                    </vx-checkbox-selection-item>
                                </div>
                            </div>

                            <div class="flex gap-2 pt-4">
                                <vx-button data-test="cancel"
                                    size="lg"
                                    type="button"
                                    color="muted-light"
                                    @click="cancel">
                                    Cancel
                                </vx-button>
                                <vx-button data-test="apply"
                                    size="lg"
                                    type="submit"
                                    block>
                                    Apply
                                    <font-awesome-icon widthAuto :icon="faCircleCheck"></font-awesome-icon>
                                </vx-button>
                            </div>
                        </form>
                    </template>
                </vx-filterable>
            </div>
            <div class="flex w-full gap-2 @xl/controls:w-auto">
                <vx-button-group size="lg" class="w-full">
                    <vx-select v-model="filters.sort_field"
                               @update:modelValue="apply"
                               :options="[
                            {
                                value: 'group_id',
                                label: 'Sort by: External ID',
                            },
                            {
                                value: 'name',
                                label: 'Sort by: Name',
                            },
                        ]"></vx-select>
                    <vx-button
                        @click.prevent="
                            filters.sort_dir = filters.sort_dir === 'asc' ? 'desc' : 'asc'
                            apply();
                        "
                        type="button"
                        color="white"
                        shadow
                        data-test="sort-direction"
                        :aria-label="`Sort ${props.modelValue.sort_dir === 'asc' ? 'ascending' : 'descending'}. Press to toggle sort direction.`">
                        <font-awesome-icon widthAuto :icon="props.modelValue.sort_dir === 'asc'
                            ? faArrowUpWideShort
                            : faArrowDownWideShort
                            "></font-awesome-icon>
                    </vx-button>
                </vx-button-group>
            </div>
        </template>
        <template v-slot:right>
            <vx-button v-if="props.newGroupHref || props.isAdminOrOwner"
                size="lg"
                block
                :href="props.newGroupHref || `/settings/teams/${props.teamId}/groups/new`">
                New Group <font-awesome-icon widthAuto :icon="faCirclePlus"></font-awesome-icon>
            </vx-button>
        </template>
    </page-controls>
</template>

<script setup>
import {
    faArrowDownWideShort,
    faArrowUpWideShort,
    faCircleCheck,
    faMagnifyingGlass,
    faCircleXmark,
    faCirclePlus,
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import {
    VxBadge,
    VxButton,
    VxButtonGroup,
    VxCheckboxSelectionItem,
    VxFilterable,
    VxInput,
    VxSelect,
} from '@voxie/frontend-components';
import GroupStatusFilterItem from '~/components/general/GroupStatusFilterItem.vue';
import { computed, nextTick, ref, useId, watch } from 'vue';
import PageControls from '~/components/general/PageControls.vue';
import FilterLabel from '~/components/general/FilterLabel.vue';
import { pluralized } from '~/components/filters';
import { filtersKey } from '~/composables/useCursorPagination';

const filters = ref({});
const props = defineProps({
    modelValue: {
        type: Object,
        required: true,
    },
    teamId: {
        type: Number,
        default: () => Spark.state.currentTeam.id,
    },
    newGroupHref: {
        type: String,
        default: null,
    },
    isAdminOrOwner: {
        type: Boolean,
        default: false,
    },
});

const setupLabelId = useId();
const setupFilter = computed({
    get() {
        const value = filters.value.setup_required;

        return {
            required: value === 1 || value === null,
            notRequired: value === 0 || value === null,
        };
    },
    set({ required, notRequired }) {
        if (required && !notRequired) {
            filters.value.setup_required = 1;
        } else if (!required && notRequired) {
            filters.value.setup_required = 0;
        } else if (required && notRequired) {
            filters.value.setup_required = null;
        } else {
            filters.value.setup_required = undefined;
        }
    },
});

const orgUnitLabelId = useId();
const orgUnitFilter = computed({
    get() {
        const value = filters.value.in_org_unit;

        return {
            inOrgUnit: value === 1 || value === null,
            notInOrgUnit: value === 0 || value === null,
        };
    },
    set({ inOrgUnit, notInOrgUnit }) {
        if (inOrgUnit && !notInOrgUnit) {
            filters.value.in_org_unit = 1;
        } else if (!inOrgUnit && notInOrgUnit) {
            filters.value.in_org_unit = 0;
        } else if (inOrgUnit && notInOrgUnit) {
            filters.value.in_org_unit = null;
        } else {
            filters.value.in_org_unit = undefined;
        }
    },
});

watch(() => filtersKey(props.modelValue), () => {
    filters.value = { ...props.modelValue };
}, {
    immediate: true,
})

const emit = defineEmits(['update:modelValue', 'apply', 'reset']);

const active = ref(false);

const toggleFilterable = (event) => {
    active.value = event;
};

const filtersCount = computed(
    () => (
        (props.modelValue.query ? 1 : 0) +
        ([null, 1, 0].includes(props.modelValue.setup_required) ? 1 : 0) +
        (Array.isArray(props.modelValue.status) && props.modelValue.status.length > 0 ? 1 : 0) +
        ([null, 1, 0].includes(props.modelValue.in_org_unit) ? 1 : 0)
    )
);

const reset = () => {
    active.value = false;
    emit('reset');
};

const cancel = async () => {
    filters.value = { ...props.modelValue };
    active.value = false;
}

const apply = async () => {
    active.value = false;
    emit('update:modelValue', { ...filters.value });
    await nextTick();
    emit('apply');
}
</script>
